/**
 * PDF card renderers for all priority domains.
 *
 * Import from here to get the HTML renderer and scoped CSS for a domain.
 * The main PDF template uses these alongside a LayoutConfig to compose the page.
 *
 * Usage:
 *   import { PositionsPdfCard, LAYOUT } from '@brainpool/profile-schema/pdf-cards';
 */

export { PositionsPdfCard } from '../domains/POSITIONS';
export { EducationPdfCard } from '../domains/EDUCATION';
export { SkillsPdfCard } from '../domains/SKILLS';
export { LanguagesPdfCard } from '../domains/LANGUAGES';
export { HonorsPdfCard } from '../domains/HONORS';
export { CertificationsPdfCard } from '../domains/CERTIFICATIONS';
export { PublicationsPdfCard } from '../domains/PUBLICATIONS';
export { PatentsPdfCard } from '../domains/PATENTS';
export { ArticlesPdfCard } from '../domains/ARTICLES';
export { CoursesPdfCard } from '../domains/COURSES';
export { OrganizationsPdfCard } from '../domains/ORGANIZATIONS';
export { ProjectsPdfCard } from '../domains/PROJECTS';
export { PhoneNumbersPdfCard } from '../domains/PHONE_NUMBERS';

export type { PdfCardDef } from '../types';

/**
 * Default layout config — defines render order, column assignment, and card heading label.
 * Import and override to customise which domains appear and where.
 */
export const LAYOUT = [
  { domain: 'POSITIONS', column: 'main' as const, label: 'Experience' },
  { domain: 'EDUCATION', column: 'main' as const, label: 'Education' },
  { domain: 'SKILLS', column: 'side' as const, label: 'Skills' },
  { domain: 'LANGUAGES', column: 'side' as const, label: 'Languages' },
  { domain: 'HONORS', column: 'side' as const, label: 'Distinctions' },
  { domain: 'CERTIFICATIONS', column: 'side' as const, label: 'Certifications' },
  { domain: 'PUBLICATIONS', column: 'overflow' as const, label: 'Publications' },
  { domain: 'PATENTS', column: 'overflow' as const, label: 'Patents' },
  { domain: 'ARTICLES', column: 'overflow' as const, label: 'Articles' },
  { domain: 'PROJECTS', column: 'overflow' as const, label: 'Projects' },
  { domain: 'ORGANIZATIONS', column: 'overflow' as const, label: 'Organisations' },
  { domain: 'COURSES', column: 'overflow' as const, label: 'Courses' },
] satisfies LayoutEntry[];

export type LayoutColumn = 'main' | 'side' | 'overflow' | 'full';

export type LayoutEntry = {
  /** Domain name key matching AnonymizedExportDomain.domain. */
  domain: string;
  /** Which column of the page grid this card occupies. */
  column: LayoutColumn;
  /** Card header label rendered above the card body. */
  label: string;
};
